import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import Graph3D from './components/Graph3D';
import NodeDetails from './components/NodeDetails';
import Controls from './components/Controls';
import Timeline from './components/Timeline';
import Soundwave from './components/Soundwave';
import Header from './components/Header';
import ArticleModal from './components/ArticleModal';
import { loadGraphData } from './utils/graphLoader';

const AppContainer = styled.div`
  width: 100vw;
  height: 100vh;
  background: #000000;
  color: #ffffff;
  overflow: hidden;
  position: relative;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
`;

const MainContent = styled.div`
  width: 100%;
  height: calc(100vh - 150px);
  position: relative;
`;

const GraphContainer = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
`;

const LoadingScreen = styled.div`
  width: 100vw;
  height: 100vh;
  background: #000000;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 24px;
`;

const LoadingSpinner = styled.div`
  width: 60px;
  height: 60px;
  border: 4px solid rgba(0, 255, 255, 0.2);
  border-top: 4px solid #00ffff;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const LoadingText = styled.div`
  color: #00ffff;
  font-size: 18px;
  font-weight: 600;
  letter-spacing: 2px;
  text-transform: uppercase;
`;

const ErrorScreen = styled.div`
  width: 100vw;
  height: 100vh;
  background: #000000;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 32px;
`;

const ErrorTitle = styled.h1`
  color: #ff0066;
  font-size: 24px;
  margin: 0;
`;

const ErrorMessage = styled.p`
  color: rgba(255, 255, 255, 0.7);
  font-size: 16px;
  max-width: 600px;
  text-align: center;
  line-height: 1.6;
`;

const RetryButton = styled.button`
  background: none;
  border: 2px solid #00ffff;
  color: #00ffff;
  padding: 12px 32px;
  font-size: 16px;
  cursor: pointer;
  border-radius: 6px;
  transition: all 0.2s;

  &:hover {
    background: #00ffff;
    color: #000000;
  }
`;

function App() {
  const [graphData, setGraphData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedNode, setSelectedNode] = useState(null);
  const [showArticleModal, setShowArticleModal] = useState(false);
  const [sidePanelOpen, setSidePanelOpen] = useState(false);
  const [filters, setFilters] = useState({
    searchTerm: '',
    selectedGroup: 'all',
    dateRange: null,
    showEdgeType: 'all'
  });

  useEffect(() => {
    loadGraph();
  }, []);

  const loadGraph = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const data = await loadGraphData();
      setGraphData(data);
    } catch (err) {
      console.error('Failed to load graph:', err);
      setError(err.message || 'Failed to load graph data');
    } finally {
      setLoading(false);
    }
  };

  const handleNodeSelect = (node) => {
    setSelectedNode(node);
    setSidePanelOpen(true);
    setShowArticleModal(false);
  };

  const handleNodeDoubleClick = (node) => {
    setSelectedNode(node);
    setShowArticleModal(true);
    setSidePanelOpen(false);
  };

  const handleClosePanel = () => {
    setSidePanelOpen(false);
    setSelectedNode(null);
  };

  const handleCloseModal = () => {
    setShowArticleModal(false);
  };

  const handleFilterChange = (newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  };

  if (loading) {
    return (
      <LoadingScreen>
        <LoadingSpinner />
        <LoadingText>Loading RAI Knowledge Graph...</LoadingText>
      </LoadingScreen>
    );
  }

  if (error) {
    return (
      <ErrorScreen>
        <ErrorTitle>Failed to Load Graph</ErrorTitle>
        <ErrorMessage>{error}</ErrorMessage>
        <ErrorMessage>
          Make sure the extraction pipeline has been run and the data files exist in /data/processed/
        </ErrorMessage>
        <RetryButton onClick={loadGraph}>Retry</RetryButton>
      </ErrorScreen>
    );
  }

  return (
    <AppContainer>
      <Header />
      
      <MainContent>
        <GraphContainer>
          <Graph3D
            data={graphData}
            onNodeClick={handleNodeSelect}
            onNodeDoubleClick={handleNodeDoubleClick}
            selectedNode={selectedNode}
            filters={filters}
          />
        </GraphContainer>

        <Controls
          graphData={graphData}
          filters={filters}
          onFilterChange={handleFilterChange}
        />

        {sidePanelOpen && selectedNode && (
          <NodeDetails
            node={selectedNode}
            onClose={handleClosePanel}
            onReadFullArticle={() => {
              setShowArticleModal(true);
              setSidePanelOpen(false);
            }}
          />
        )}
      </MainContent>

      <Timeline
        nodes={graphData?.nodes || []}
        onNodeSelect={handleNodeSelect}
      />

      <Soundwave />

      {showArticleModal && selectedNode && (
        <ArticleModal
          node={selectedNode}
          onClose={handleCloseModal}
        />
      )}
    </AppContainer>
  );
}

export default App;